/**
 * useGamePause Hook
 *
 * Custom hook for managing game pause state with:
 * - isPaused state and ref (for synchronous access in async functions)
 * - waitForUnpause utility for pausing async loops
 * - Capacitor App listeners for background/foreground events
 * - Auto-save when app goes to background
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { debugLog } from '../utils/debugUtils';
import gameLogger from '../services/GameLogger';
import gameStateManager from '../services/GameStateManager';

/**
 * Wait for the game to unpause
 * @param {React.MutableRefObject<boolean>} isPausedRef - Ref to check pause state
 * @param {number} checkInterval - How often to check (ms)
 * @returns {Promise<void>}
 */
export const waitForUnpause = async (isPausedRef, checkInterval = 100) => {
    while (isPausedRef.current) {
        await new Promise(resolve => setTimeout(resolve, checkInterval));
    }
};

/**
 * Hook for managing game pause state
 * 
 * @param {string} gameType - 'ronda' or 'baciga'
 * @param {Function} getGameState - Function to get current game state for saving
 * @returns {Object} { isPaused, setIsPaused, isPausedRef, waitForUnpause }
 */
export const useGamePause = (gameType, getGameState) => {
    const [isPaused, setIsPaused] = useState(false);
    const isPausedRef = useRef(false);
    const hasInitialized = useRef(false);
    const getGameStateRef = useRef(getGameState);

    // Keep refs in sync
    useEffect(() => {
        isPausedRef.current = isPaused;
    }, [isPaused]);

    useEffect(() => {
        getGameStateRef.current = getGameState;
    }, [getGameState]);

    // Handle pause toggle
    const handlePauseToggle = useCallback((paused) => {
        setIsPaused(paused);
        isPausedRef.current = paused;
        gameLogger.log('pause_toggle', { paused, gameType });
    }, [gameType]);

    // Auto-save game state
    const autoSave = useCallback(async () => {
        if (getGameStateRef.current) {
            try {
                const state = getGameStateRef.current();
                await gameStateManager.saveGameState(gameType, state);
                gameLogger.log('auto_save', { gameType, reason: 'app_background' });
                debugLog('[useGamePause] Auto-saved game state');
            } catch (error) {
                console.error('[useGamePause] Failed to auto-save:', error);
            }
        }
    }, [gameType]);

    // Capacitor App listeners for background/foreground
    useEffect(() => {
        if (!Capacitor.isNativePlatform()) {
            // Web platform - use visibilitychange
            const handleVisibilityChange = () => {
                // Ignore the first visibility event to prevent auto-pause on initial load
                if (!hasInitialized.current) {
                    hasInitialized.current = true;
                    debugLog('[useGamePause] First visibility event - ignoring to prevent auto-pause on load');
                    return;
                }

                if (document.hidden) {
                    debugLog('[useGamePause] App hidden (web) - pausing and saving');
                    setIsPaused(true);
                    isPausedRef.current = true;
                    gameLogger.log('visibility_hidden', { platform: 'web' });
                    autoSave();
                }
            };

            document.addEventListener('visibilitychange', handleVisibilityChange);
            return () => {
                document.removeEventListener('visibilitychange', handleVisibilityChange);
            };
        }

        // Native platform - use Capacitor App plugin
        let appListener = null;

        const setupCapacitorListeners = async () => {
            try {
                const { App } = await import('@capacitor/app');

                appListener = await App.addListener('appStateChange', async ({ isActive }) => {
                    debugLog('[useGamePause] App state changed, isActive:', isActive);

                    if (!isActive) {
                        // App going to background
                        setIsPaused(true);
                        isPausedRef.current = true;
                        gameLogger.log('app_background', { platform: Capacitor.getPlatform() });
                        await autoSave();
                    }
                    // Note: We don't auto-resume when app comes back - let user do it via modal
                });

                debugLog('[useGamePause] Capacitor App listeners setup');
            } catch (error) {
                console.warn('[useGamePause] Failed to setup Capacitor App listeners:', error);
            }
        };

        setupCapacitorListeners();

        return () => {
            if (appListener) {
                appListener.remove();
            }
        };
    }, [autoSave]);

    // Wait for unpause utility bound to this instance's ref
    const waitForUnpauseBound = useCallback(async () => {
        await waitForUnpause(isPausedRef);
    }, []);

    return {
        isPaused,
        setIsPaused,
        isPausedRef,
        handlePauseToggle,
        waitForUnpause: waitForUnpauseBound,
    };
};

export default useGamePause;
